// The engagement builder's draft: everything a customer (or staff on their behalf)
// puts together before it becomes an Engagement. Shared by the public /start page,
// the customer portal and ops, so the shape lives here and nowhere else.

export type GearPick = { assetTag: string; name: string; pieces: number };
export type UploadedModel = { name: string; size: number; signedId?: string; progress: number; error?: string };
export type ScanDetails = { objectSize: string; objectCount: string; surfaces: string[]; accuracy: string; purpose: string[]; location: string; notes: string };

export type Draft = {
    interests: string[];
    projectName: string;
    details: string;
    startsOn: string;
    budget: string;
    gear: GearPick[];
    consult: boolean;
    models: UploadedModel[];
    scan: ScanDetails;
    crew: string[];
    name: string;
    email: string;
    company: string;
    phone: string;
};

export const emptyScan = (): ScanDetails => ({objectSize: '', objectCount: '', surfaces: [], accuracy: '', purpose: [], location: '', notes: ''});

export const emptyDraft = (overrides: Partial<Draft> = {}): Draft => ({
    interests: [], projectName: '', details: '', startsOn: '', budget: '', gear: [], consult: false, models: [], scan: emptyScan(),
    crew: [], name: '', email: '', company: '', phone: '', ...overrides,
});

// Who is building it. Decides which steps show and how the submit is addressed.
export type Host = 'public' | 'customer' | 'ops';
export type StepKey = 'customer' | 'what' | 'gear' | 'models' | 'scan' | 'crew' | 'details' | 'contact';

export const STEP_LABEL: Record<StepKey, string> = {
    customer: 'Customer', what: 'What', gear: 'Gear', models: 'Models', scan: 'Scanning', crew: 'Crew', details: 'Details', contact: 'You',
};

// Steps appear based on what was picked: [Customer] → What → [Gear] → [Models] → [Scanning] → [Crew] → Details → [You].
export const stepsFor = (draft: Draft, host: Host): StepKey[] => [
    ...(host === 'ops' ? (['customer'] as StepKey[]) : []),
    'what',
    ...(draft.interests.includes('rental') ? (['gear'] as StepKey[]) : []),
    ...(draft.interests.includes('printing') ? (['models'] as StepKey[]) : []),
    ...(draft.interests.includes('scanning') ? (['scan'] as StepKey[]) : []),
    // Crew only makes sense for shoots and streams.
    ...(draft.interests.some((i) => ['production', 'photography', 'live_event'].includes(i)) ? (['crew'] as StepKey[]) : []),
    'details',
    ...(host === 'public' ? (['contact'] as StepKey[]) : []),
];

export const uploading = (draft: Draft) => draft.models.some((m) => !m.signedId && !m.error);

export const canContinue = (step: StepKey, draft: Draft, customerChosen: boolean): boolean => {
    switch (step) {
        case 'customer': return customerChosen;
        case 'what': return draft.interests.length > 0;
        case 'models': return !uploading(draft);
        case 'details': return draft.details.trim().length > 0;
        case 'contact': return Boolean(draft.name.trim()) && /\S+@\S+\.\S+/.test(draft.email);
        default: return true;
    }
};

// Mirrors Engagement::INTERESTS on the server.
export const INTERESTS = [
    {key: 'production', label: 'Video production', blurb: 'Commercials, music videos, narrative, docs'},
    {key: 'photography', label: 'Photography', blurb: 'Product, portrait, event, architecture'},
    {key: 'scanning', label: '3D scanning', blurb: 'Props, sets, people, products for VFX and previs'},
    {key: 'printing', label: '3D printing', blurb: 'Prototypes, props, replacement parts'},
    {key: 'rental', label: 'Gear rental', blurb: 'Cameras, lenses, lighting, audio, drones'},
    {key: 'live_event', label: 'Live event / stream', blurb: 'Multi-camera switching and streaming'},
    {key: 'marketing', label: 'Marketing', blurb: 'Content, campaigns, SEO'},
    {key: 'other', label: 'Something else', blurb: 'Tell us about it'},
] as const;

export const BUDGETS = ['Under $1k', '$1k – $5k', '$5k – $20k', '$20k+', 'Not sure yet'];
export const MODEL_EXTENSIONS = ['.stl', '.obj', '.3mf', '.step', '.stp', '.ply', '.glb', '.gltf', '.fbx', '.zip'];
export const MAX_MODEL_MB = 200;

// Mirrors Engagement::SCAN_* on the server.
export const SCAN_SIZES = [
    {key: 'handheld', label: 'Handheld', blurb: 'fits in a hand — jewellery, parts, small props'},
    {key: 'tabletop', label: 'Tabletop', blurb: 'up to ~1 m — props, products, busts'},
    {key: 'furniture', label: 'Furniture-sized', blurb: '1–3 m — set pieces, sculptures, people'},
    {key: 'vehicle', label: 'Vehicle-sized', blurb: 'cars, large machinery'},
    {key: 'room', label: 'Room / set', blurb: 'interiors, stages'},
    {key: 'building', label: 'Building / exterior', blurb: 'facades, locations'},
];
export const SCAN_SURFACES = [
    {key: 'matte', label: 'Matte / painted'},
    {key: 'shiny', label: 'Shiny or metallic', blurb: 'needs scanning spray'},
    {key: 'dark', label: 'Black or very dark', blurb: 'needs scanning spray'},
    {key: 'transparent', label: 'Clear / glass', blurb: 'needs scanning spray'},
    {key: 'fine_detail', label: 'Fine detail / texture'},
    {key: 'hair_or_fur', label: 'Hair, fur, fabric'},
    {key: 'organic', label: 'People / organic'},
];
export const SCAN_ACCURACIES = [
    {key: 'visual', label: 'Looks right', blurb: 'VFX, previs, visualisation'},
    {key: 'engineering', label: 'Engineering', blurb: 'fits and prints; sub-millimetre'},
    {key: 'metrology', label: 'Metrology', blurb: 'inspection-grade with a report'},
];
export const SCAN_PURPOSES = [
    {key: 'vfx', label: 'VFX asset'}, {key: 'previs', label: 'Previs / virtual production'}, {key: 'cad_reverse_engineering', label: 'CAD / reverse engineering'},
    {key: 'printing', label: '3D printing'}, {key: 'archival', label: 'Archival'}, {key: 'inspection', label: 'Inspection / QA'},
];
export const SCAN_LOCATIONS = [{key: 'studio', label: 'Bring it to the studio'}, {key: 'on_site', label: 'Come to us (on site)'}];

// The CreateEngagementInput for a draft. Contact fields are sent only by the public host;
// ops passes the customer it chose; a signed-in customer needs neither.
export const toInput = (draft: Draft, opts: { host: Host; customerId?: string }) => ({
    interests: draft.interests,
    projectName: draft.projectName.trim() || null,
    details: draft.details.trim(),
    startsOn: draft.startsOn || null,
    budget: draft.budget || null,
    assetTags: draft.interests.includes('rental') ? draft.gear.map((g) => g.assetTag) : [],
    wantsConsultation: draft.interests.includes('rental') && draft.consult,
    modelFileIds: draft.interests.includes('printing') ? draft.models.flatMap((m) => (m.signedId ? [m.signedId] : [])) : [],
    talentSlugs: draft.crew,
    scanDetails: draft.interests.includes('scanning') ? {
        objectSize: draft.scan.objectSize || null,
        objectCount: draft.scan.objectCount ? Number(draft.scan.objectCount) : null,
        surfaces: draft.scan.surfaces,
        accuracy: draft.scan.accuracy || null,
        purpose: draft.scan.purpose,
        location: draft.scan.location || null,
        notes: draft.scan.notes.trim() || null,
    } : null,
    customerId: opts.host === 'ops' ? opts.customerId ?? null : null,
    name: opts.host === 'public' ? draft.name.trim() : null,
    email: opts.host === 'public' ? draft.email.trim() : null,
    company: opts.host === 'public' ? draft.company.trim() || null : null,
    phone: opts.host === 'public' ? draft.phone.trim() || null : null,
});

// The public draft survives navigation and the Facebook sign-in round trip.
const DRAFT_KEY = 'lwm.engagement.draft';

export const loadDraft = (): Draft | null => {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        return raw ? {...emptyDraft(), ...(JSON.parse(raw) as Partial<Draft>)} : null;
    } catch {
        return null;
    }
};

export const saveDraft = (draft: Draft) => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* storage may be unavailable */ }
};

export const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
};
